import React, { Suspense } from "react";
import { createLazyComponent } from "../../utils/codeSplitting";

// Loading fallback components
const PageLoadingFallback: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
  </div>
);

const ComponentLoadingFallback: React.FC = () => (
  <div className="flex items-center justify-center p-8">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
  </div>
);

// Lazy-loaded page components
export const LazyProviderDetailPage = createLazyComponent(
  () => import("../../pages/ProviderDetailPage"),
  PageLoadingFallback
);

export const LazyProviderListPage = createLazyComponent(
  () => import("../../pages/ProviderListPage"),
  PageLoadingFallback
);

export const LazyRegisterPage = createLazyComponent(
  () => import("../../pages/RegisterPage"),
  PageLoadingFallback
);

export const LazyProviderRegistrationFlow = createLazyComponent(
  () => import("../ProviderRegistrationFlow"),
  PageLoadingFallback
);

export const LazyProviderRegistration = createLazyComponent(
  () => import("../ProviderRegistrationLegacyRedirect"),
  PageLoadingFallback
);

export const LazyAdminProviderManagement = createLazyComponent(
  () => import("../AdminProviderManagement"),
  PageLoadingFallback
);

// Lazy-loaded UI components
export const LazyModal = createLazyComponent(
  () => import("../ui/Modal"),
  ComponentLoadingFallback
);

export const LazyButton = createLazyComponent(
  () => import("../ui/Button"),
  ComponentLoadingFallback
);

export const LazyBreadcrumb = createLazyComponent(
  () => import("../ui/Breadcrumb"),
  ComponentLoadingFallback
);

export const LazyProviderSearchErrorBoundary = createLazyComponent(
  () => import("../ui/ProviderSearchErrorBoundary"),
  ComponentLoadingFallback
);

// Lazy-loaded admin components
export const LazyAdminProviderSearch = createLazyComponent(
  () => import("../admin/AdminProviderSearch"),
  ComponentLoadingFallback
);

export const LazyAdminProviderForm = createLazyComponent(
  () => import("../admin/AdminProviderForm"),
  ComponentLoadingFallback
);

export const LazyAdminProviderCard = createLazyComponent(
  () => import("../admin/AdminProviderCard"),
  ComponentLoadingFallback
);

// Lazy-loaded utility components
export const LazyConfirmationModal = createLazyComponent(
  () => import("../ui/ConfirmationModal"),
  ComponentLoadingFallback
);

export const LazyOptimizedLoadingSpinner = createLazyComponent(
  () => import("../OptimizedLoadingSpinner"),
  ComponentLoadingFallback
);

export const LazyPerformanceMonitor = createLazyComponent(
  () => import("../PerformanceMonitor"),
  ComponentLoadingFallback
);

// Preload functions for better UX
export const preloadProviderPages = () => {
  import("../../pages/ProviderDetailPage");
  import("../../pages/ProviderListPage");
  import("../../pages/RegisterPage");
};

export const preloadAdminComponents = () => {
  import("../AdminProviderManagement");
  import("../admin/AdminProviderSearch");
  import("../admin/AdminProviderForm");
  import("../admin/AdminProviderCard");
};

export const preloadUIComponents = () => {
  import("../ui/Modal");
  import("../ui/Button");
  import("../ui/Breadcrumb");
  import("../ui/ConfirmationModal");
};

// Route-based preloading (covers all major routes for hover/nav preload)
export const preloadRoute = (routeName: string) => {
  switch (routeName) {
    case "provider-detail":
      import("../../pages/ProviderDetailPage");
      break;
    case "provider-list":
      import("../../pages/ProviderListPage");
      break;
    case "register":
      import("../../pages/RegisterPage");
      break;
    case "admin":
    case "admin-dashboard":
      import("../../pages/AdminDashboard");
      preloadAdminComponents();
      break;
    case "provider-layout":
    case "provider-profile":
      import("../provider/ProviderLayout");
      import("../provider/ProviderProfile");
      break;
    case "schedule-appointment":
      import("../../pages/ScheduleAppointmentPage");
      break;
    case "patient-dashboard":
      import("../../pages/PatientDashboard");
      break;
    case "patient-profile":
      import("../PatientProfile");
      break;
    case "social-worker-dashboard":
      import("../../pages/SocialWorkerDashboard");
      break;
    default:
      break;
  }
};
